// The lifecycle LADDER's rule — which rung a task stands on — apart from how a
// rung is worn on screen (`client/activities/model/ladder.ts`), so the backend
// can say a task's status in the same words the screens do (AI-assistant
// activity model spec, D2; the ladder itself is the task-commit UX spec,
// decision 2). The backend may not import `client/*`; this module has no UI.
//
// The task's PLAN status, whether it is committed, and Trusstor's word for the
// activity fold into one rung:
//   · `committed` is a POSITION, not a status — a committed task is still
//     `planned` in the plan until somebody marks it done;
//   · everything between Committed and Done is the activity's status;
//   · the last rung before Done is a SUGGESTION ("areas done"), never a
//     transition — done is human (invariant 6).

/** Trusstor's word for an activities group (`core/shared/activity-status.ts` holds the schema). */
export type ActivityGroupStatus = 'planned' | 'inProgress' | 'delayed' | 'overdue' | 'complete';

/** What the ladder needs of a task: its plan status and whether it is committed. */
export interface ILadderTask {
  status: 'planned' | 'blocked' | 'done';
  committed: boolean;
}

export type LadderRung =
  | 'planned'
  | 'blocked'
  | 'committed'
  | 'inProgress'
  | 'delayed'
  | 'overdue'
  | 'areasDone'
  | 'done';

/** Every rung, in ladder order — the Scheduler's status filter offers them all
 *  (an uncommitted task only ever stands on Planned or Blocked). */
export const LADDER_RUNGS: readonly LadderRung[] = [
  'planned',
  'blocked',
  'committed',
  'inProgress',
  'delayed',
  'overdue',
  'areasDone',
  'done',
];

/** Which of the three stops the task stands on: Planned → Committed → Done. */
export type LadderStep = 0 | 1 | 2;

export interface ILadder {
  rung: LadderRung;
  step: LadderStep;
}

/** What the ladder needs to know about the activity behind a committed task. */
export interface ILadderActivity {
  activityStatus: ActivityGroupStatus | null;
}

const RUNG_BY_ACTIVITY: Record<ActivityGroupStatus, LadderRung> = {
  planned: 'committed',
  inProgress: 'inProgress',
  delayed: 'delayed',
  overdue: 'overdue',
  complete: 'areasDone',
};

/**
 * The rung a Trusstor activity stands on by its status alone — a monitor
 * activity seen without its task. An unknown status (Trusstor's `deleted`)
 * reads as plainly committed, as a status not yet arrived does.
 */
export function rungOfActivityStatus(status: string): LadderRung {
  return (RUNG_BY_ACTIVITY as Record<string, LadderRung>)[status] ?? 'committed';
}

export function ladderOf(
  task: ILadderTask,
  activity?: ILadderActivity,
): ILadder {
  if (task.status === 'done') return { rung: 'done', step: 2 };
  if (task.status === 'blocked') return { rung: 'blocked', step: 0 };
  if (!task.committed) return { rung: 'planned', step: 0 };
  // A committed task whose status has not arrived yet (feed still loading)
  // reads as plainly "committed".
  const status = activity?.activityStatus;
  return { rung: status ? RUNG_BY_ACTIVITY[status] : 'committed', step: 1 };
}
